/**
 * CoTrainer Frontend — Bootstrap (Phase 3.5 §5 Shell-Refactor).
 *
 * Flow:
 *   1. /login/callback → Token aus Hash setzen + Hash-Cleanup
 *   2. probeAuth → setCurrentUser
 *   3. Kein User → Login-Page rendern (Shell wird NICHT gemountet)
 *   4. User → mountShell + registerAllRoutes + startRouter
 *
 * Shell + Router rendert die View in `#view-container` (innerhalb der Shell).
 * Login-Page läuft außerhalb der Shell weil sie kein Login-Layout braucht.
 */

import "./styles/form-inputs.css";
import { handleOAuthCallback } from "./auth/callback";
import { probeAuth } from "./auth/auth-guard";
import { setToken } from "./auth/token";
import { mountShell } from "./components/app-shell";
import { setCurrentUser, startRouter } from "./router/router";
import { registerAllRoutes } from "./router/routes";
import { renderLoginPage } from "./views/login-page";
import { renderPublicAuthPage } from "./views/auth-pages";
import { renderAuthClaimProfile } from "./views/auth-claim-profile";
import { appDateFormat, appOutcomeLabels, claimProfiles } from "./api/client";
import { setAppDateFormat } from "./lib/dates";
import { setOutcomeLabels } from "./lib/outcome-labels";
import { getToken, clearToken } from "./auth/token";
import { createIcons, icons } from "lucide";

// HR-Views (Phase 8) nutzen Lucide-Icons via `data-l`-Attribut + globalem
// `lucide.createIcons`. Wir binden das komplette Icon-Set einmalig an `window`,
// sodass jede View nach ihrem `innerHTML` `lucide.createIcons({nameAttr:"data-l"})`
// aufrufen kann. (Bundle-Optimierung — nur genutzte Icons — später möglich.)
(window as unknown as { lucide: { createIcons: (o?: Record<string, unknown>) => void } }).lucide = {
  createIcons: (opts: Record<string, unknown> = {}) => createIcons({ icons, ...opts }),
};

// Public-Auth-Seiten (ohne Token erreichbar) — werden VOR probeAuth gerendert,
// da der Router erst nach Login läuft.
const PUBLIC_AUTH_PATHS = new Set(["/aktivieren", "/passwort-reset", "/passwort-vergessen"]);

async function bootstrap(): Promise<void> {
  const app = document.getElementById("app");
  if (!app) {
    console.error("#app Element nicht gefunden");
    return;
  }

  // 0. Public-Auth-Seite (Aktivieren/Reset/Vergessen)? Direkt rendern, ohne Auth.
  if (PUBLIC_AUTH_PATHS.has(window.location.pathname)) {
    renderPublicAuthPage(app, window.location.pathname);
    return;
  }

  // 1. Callback-Pfad? Token-Hash konsumieren
  const parsed = handleOAuthCallback();
  if (parsed) {
    setToken(parsed.token, parsed.exp);
  }

  // 2. UserContext probe
  const user = await probeAuth();
  setCurrentUser(user);

  if (!user) {
    // Phase 7.7: Token vorhanden, aber kein Profil (/me 403)? → Profil-Selbst-
    // zuordnung anbieten, sofern Workspace-Mail + wählbare Profile. Sonst Login.
    if (getToken()) {
      try {
        const claim = await claimProfiles();
        renderAuthClaimProfile(app, claim);
        return;
      } catch {
        // keine Workspace-Mail / Claim deaktiviert → normaler Login
        clearToken();
      }
    }
    renderLoginPage(app, { redirectTo: pendingTargetFromLocation() });
    return;
  }

  // 2b. App-weite Anzeige-Settings parallel laden: Datumsformat (DATE_FORMAT;
  // Default "de") + konfigurierbare Outcome-Labels (M1–M8/F1–F9). Beide fail-safe
  // — schlägt eines fehl, bleibt der jeweilige Default/Fallback.
  const [dateRes, labelsRes] = await Promise.allSettled([appDateFormat(), appOutcomeLabels()]);
  if (dateRes.status === "fulfilled") setAppDateFormat(dateRes.value.date_format);
  if (labelsRes.status === "fulfilled") setOutcomeLabels(labelsRes.value.outcome_labels);

  // 3. Shell mounten + Router starten
  mountShell(app, user);
  registerAllRoutes();
  startRouter();
}

function pendingTargetFromLocation(): string | undefined {
  const p = window.location.pathname;
  if (!p || p === "/" || p === "/login/callback") return undefined;
  return p + window.location.search;
}

void bootstrap();
